import {AttrsSchema, Component} from 'panel';

import {ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {formatText} from 'common/util/format-text';
import {panel2react} from 'common/util/react';

import {
  ActionEvent,
  BannerAction,
  BannerAttrs,
  BannerJsxProps,
  BannerSize,
  BannerTheme,
  BannerType,
  CallToAction,
} from './types';
import {template} from './template';
import css from './index.inline.styl';

const iconFromTheme = new Map<BannerTheme, string>([
  [BannerTheme.Base, `idea`],
  [BannerTheme.Error, `warning`],
  [BannerTheme.Info, `info`],
  [BannerTheme.Success, `circle-checkmark`],
  [BannerTheme.Warn, `warning`],
]);

const buttonTypeFromTheme = new Map<BannerTheme, ButtonType>([
  [BannerTheme.Error, ButtonType.Destructive],
  [BannerTheme.Info, ButtonType.Info],
  [BannerTheme.Success, ButtonType.Success],
  [BannerTheme.Warn, ButtonType.Warn],
]);

const trackBannerShownEventName = `[mp-banner] Banner Shown`;
const trackBannerCtaClick = `[mp-banner] Banner CTA Clicked`;

export class Banner extends Component<EmptyObj, BannerAttrs> {
  static get attrsSchema(): AttrsSchema<BannerAttrs> {
    return {
      ...super.attrsSchema,
      'body-text': `string`,
      ctas: `json`,
      'header-text': `string`,
      icon: {type: `string`, default: `from-theme`},
      size: {type: `string`, enum: Object.values(BannerSize), default: BannerSize.Medium},
      theme: {type: `string`, enum: Object.values(BannerTheme), default: BannerTheme.Info},
      type: {type: `string`, enum: Object.values(BannerType), default: BannerType.Floating},
      'banner-tracking-type': `string`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {},
      helpers: {
        BannerAction,
        BannerSize,
        BannerType,
        formatText,
        getIcon: () =>
          this.attr(`icon`) === `from-theme` ? iconFromTheme.get(this.attr(`theme`) as BannerTheme) : this.attr(`icon`),
        getButtonType: (): ButtonType => buttonTypeFromTheme.get(this.attr(`theme`) as BannerTheme),
        getBannerTypeProp: (): string => {
          return this.attr(`banner-tracking-type`) || this.attr(`body-text`);
        },
        trackBannerCtaClick: (cta: CallToAction): void => {
          const {actionName, href, text, trackEventName, trackProps} = cta;

          if (trackEventName) {
            // Don't break legacy tracking for these banners
            this.dispatchEvent(
              new CustomEvent(`track`, {
                bubbles: true,
                composed: true,
                cancelable: true,
                detail: {
                  eventName: trackEventName,
                  props: trackProps || {
                    'Action Name': actionName,
                  },
                },
              }),
            );
          }

          // New tracking: "[mp-banner] Banner CTA Clicked" event
          this.dispatchEvent(
            new CustomEvent(`track`, {
              bubbles: true,
              composed: true,
              cancelable: true,
              detail: {
                eventName: trackBannerCtaClick,
                props: {
                  'Action Name': actionName,
                  'Banner Type': this.helpers.getBannerTypeProp(),
                  href,
                  text,
                  ...(trackProps || {}),
                },
              },
            }),
          );
        },
        trackBannerShown: (): void => {
          this.dispatchEvent(
            new CustomEvent(`track`, {
              bubbles: true,
              composed: true,
              cancelable: true,
              detail: {
                eventName: trackBannerShownEventName,
                props: {
                  'Banner Type': this.helpers.getBannerTypeProp(),
                  'Banner Message': this.attr(`body-text`),
                },
              },
            }),
          );
        },
        handleCtaButtonClick: (cta: CallToAction): void => {
          const {actionName, href, hrefTarget} = cta;

          if (actionName && actionName !== BannerAction.None) {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {actionName}}) as ActionEvent);
          }

          this.helpers.trackBannerCtaClick(cta);

          // if user wishes to make a link button
          if (href) {
            window.open(href, hrefTarget || `_blank`);
          }
        },
      },
    };
  }

  public connectedCallback(): void {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this.helpers.trackBannerShown();
    });
  }
}

export const BannerEl = defineAndMakeJsxFactory<BannerJsxProps>(`mp-banner`, Banner);
export const BannerReact = panel2react<BannerJsxProps>(`mp-banner`, Banner);
